<template>
	<v-expansion-panel class="log-event"
		:class="{'spot-block': isSpotBlock, 'local-break': isLocalBreak, 'voice-track': isVoiceTrack}"
		rounded="0"
		elevation="0">
		<v-expansion-panel-title expand-icon=""
			hide-actions
			:disabled="event.empty"
			:readonly="!(isSpotBlock || isLocalBreak || isDVTBlock)"
			:style="{'cursor': (isSpotBlock || isLocalBreak || isDVTBlock) ? 'pointer' : 'default'}"
			@mouseover="hoverToPaste = true"
			@mouseleave="hoverToPaste = false">
			<v-hover v-slot="{ props }">
				<v-card v-bind="props" v-if="!event.empty" class="log-event-container"
					variant="outlined"
					rounded="0"
					width="100%"
					:class="{
						'disabled-event': isDisabledEvent,
						'skipped-event': event.Skipped,
						'now-playing': isCurrentEvent
					}"
					@dblclick="openAssetManagement"
					@contextmenu.prevent="openContextMenu">
					<div class="asset-container d-flex align-center" :class="{'now-playing': isNowPlaying}">

						<!-- Start Time and Now Playing label -->
						<div class="d-flex flex-column airtime pl-2">
							<div v-if="isNowPlaying" class="now-playing-label">
								Now Playing
								<v-icon size="18" class="ml-1" icon="mdi-volume-high"/>
							</div>
							<v-tooltip location="bottom" text="Airtime">
								<template v-slot:activator="{ props }">
									<div v-bind="props" class="event-field"
										:class="[isNowPlaying ? 'text-h3' : 'text-body-1']">
										{{ airTime }}
									</div>
								</template>
							</v-tooltip>
						</div>

						<!-- Event Info -->
						<div class="event-field text-truncate"
							:class="[isNowPlaying || isETM || isModeSelectionEvent ? 'text-h3': 'text-body-1']">

							<!-- Asset -->
							<div v-if="isPlayAsset">
								<!-- Asset Info (Icon / Album / Title - Artist) -->
								<div v-if="asset && !missingAudioPath" class="d-flex align-center text-truncate">
									<div class="d-flex flex-column text-truncate">
										<div class="text-truncate">
											<v-tooltip location="bottom" text="Title">
												<template v-slot:activator="{ props }">
													<span v-bind="props">{{ asset.Title }}</span>
												</template>
											</v-tooltip>
											-
											<v-tooltip location="bottom" text="Artist">
												<template v-slot:activator="{ props }">
													<span v-bind="props">{{ asset.Artist }}</span>
												</template>
											</v-tooltip>
										</div>
										<v-tooltip v-if="asset.Album" location="bottom" text="Album">
											<template v-slot:activator="{ props }">
											<span v-bind="props" class="text-caption text-truncate">
												{{ asset.Album }}
											</span>
											</template>
										</v-tooltip>
									</div>
								</div>
								<div v-else>
									<v-tooltip location="bottom" text="Missing Audio">
										<template v-slot:activator="{ props }">
											<v-icon v-bind="props" icon="mdi-ear-hearing-off" size="small"/>
										</template>
									</v-tooltip>
									Missing Asset
								</div>

								<!-- Unrecorded VoiceTrack -->
								<div v-if="unrecordedVoiceTrack" class="d-flex align-center text-truncate">
									<v-icon class="asset-type-icon" icon="mdi-alert"/>
									Unrecorded VoiceTrack
								</div>

								<!-- Missing Audio -->
								<div v-if="isMissingAudio" class="d-flex align-center">
									<v-tooltip location="bottom" text="Missing Audio">
										<template v-slot:activator="{ props }">
											<v-icon v-bind="props" icon="mdi-close-circle"/>
										</template>
									</v-tooltip>
									<v-tooltip location="bottom" :text="assetType?.Description ?? 'Unknown Asset Type'">
										<template v-slot:activator="{ props }">
											<v-icon v-bind="props" class="asset-type-icon" :icon="assetTypeIcon"/>
										</template>
									</v-tooltip>
									<v-tooltip location="bottom" text="Title">
										<template v-slot:activator="{ props }">
											<span v-bind="props" class="event-field text-truncate">
												{{ asset.Title }}
											</span>
										</template>
									</v-tooltip>
								</div>
							</div>

							<!-- Spot Block -->
							<div v-if="isSpotBlock" class="d-flex align-center">
								<div class="event-field d-flex align-center">
									{{
										event.ChildPlaylist?.TargetLength > 0 ?
										`Spot Block - ${secondsToFormat(
											event.ChildPlaylist?.TargetLength,
											"mm:ss",
											{trim: "left", stopTrim: "m"}
										)}` :
										"Spot Block"
									}}
									<v-icon class="ml-2 panel-arrow" icon="mdi-chevron-down"/>

									<div v-if="spotBlockContainsLiveRead">
										<v-tooltip location="bottom">
											<template v-slot:activator="{ props }">
												<v-chip v-bind="props" label variant="outlined" class="live-read">
													LIVE READ
												</v-chip>
											</template>
											<span>There is a Live Read inside this Spotblock</span>
										</v-tooltip>
									</div>
								</div>
							</div>

							<!-- DVT Block -->
							<div v-if="isDVTBlock" class="d-flex align-center">
								<v-icon icon="mdi-forum" class="asset-type-icon"/>
								<div class="event-field d-inline-flex">
									DVT Block
									<v-icon class="ml-2 panel-arrow" icon="mdi-chevron-down"/>
								</div>
							</div>

							<!-- Local Break -->
							<div v-if="isLocalBreak" class="d-flex align-center">
								<v-icon class="asset-type-icon" :icon="localBreakIcon"/>
								<div class="event-field d-inline-flex">
									Local Break
									<v-icon class="ml-2 panel-arrow" icon="mdi-chevron-down"/>
								</div>
							</div>

							<!-- Live Read -->
							<div v-if="isLiveRead" class="d-flex align-center">
								<div class="event-field d-inline-flex">
									{{
										event.LinearAdData.spotLength > 0 ?
										`${event.LinearAdData.displayName || event.LinearAdData.title ||
										   event.LinearAdData.description} - ${secondsToFormat(
											event.LinearAdData.spotLength,
											"mm:ss",
											{trim: "left", stopTrim: "m"}
										)}` :
										`${event.LinearAdData.displayName || event.LinearAdData.title ||
										   event.LinearAdData.description}`
									}}
								</div>
								<v-tooltip location="bottom">
									<template v-slot:activator="{ props }">
										<v-chip v-bind="props" label variant="outlined" class="live-read ml-4">
											LIVE READ
										</v-chip>
									</template>
									<span>Live Read</span>
								</v-tooltip>
							</div>

							<!-- Comment -->
							<div v-if="isComment" class="">
								{{ description }}
							</div>

							<!-- Rejoin -->
							<div v-if="isRejoin" class="ml-9">
								Rejoin
							</div>

							<!-- Start of Live Show -->
							<div v-if="isStartLiveShow" class="d-flex align-center">
								<v-icon class="asset-type-icon" :icon="iconStartLiveShow"/>
								<span class="event-field mx-4">
										Start Live Show
									</span>
							</div>

							<!-- End of Live Show -->
							<div v-if="isEndLiveShow" class="d-flex align-center">
								<v-icon class="asset-type-icon" :icon="iconEndLiveShow"/>
								<span class="event-field mx-4">
										End Live Show
									</span>
							</div>

						</div>

						<!-- Timers -->
						<div class="event-field ml-auto text-body-1" :class="{'text-h3': isNowPlaying}">
							<div class="d-flex align-center mx-3">
								<div v-if="isPlayAsset && asset && hasAudio && !isLiveRead"
									class="d-flex">
									<v-tooltip location="bottom" text="Duration">
										<template v-slot:activator="{ props }">
											<div v-bind="props">
												{{
													secondsToFormat(duration, "hh:mm:ss", {trim: "left", stopTrim: "m"})
												}}
											</div>
										</template>
									</v-tooltip>
									<span class="mx-1">|</span>
									<v-tooltip location="bottom" text="Intro">
										<template v-slot:activator="{ props }">
											<div v-bind="props">
												{{ secondsToFormat(intro, "mm:ss") }}
											</div>
										</template>
									</v-tooltip>
								</div>
								<div v-else-if="isSpotBlock || isLocalBreak || isLiveRead">
									{{ secondsToFormat(duration, "mm:ss") }}
								</div>
								<div v-else>
									<div v-if="event.AssetID" class="d-flex align-center">
										<v-icon icon="mdi-alert" color="#C6002B"/>
										<span class="event-field">
											Missing Audio
										</span>
									</div>
								</div>
							</div>
						</div>

						<div class="d-flex">
							<v-menu v-model="contextMenu.open" persistent min-width="150"
								:target="[contextMenu.x, contextMenu.y]"
								:close-on-content-click="false">
								<template v-slot:activator="{ props }">
									<v-btn v-bind="props" icon="mdi-dots-vertical" variant="text"
										density="compact"/>
								</template>
								<v-list>
									<v-tooltip activator="parent" location="bottom" text="Missing Audio"
										:disabled="!!asset">
										<template v-slot:activator="{ props }">
											<v-list-item v-bind="props" v-if="isPlayAsset"
												:disabled="!asset || auditionAudioMenu.open"
												@click="openAuditionAudioWidget($event, asset, 'log-list')">
												<v-list-item-title class="text-body-2">
													Audition Audio
												</v-list-item-title>
											</v-list-item>
										</template>
									</v-tooltip>
									<v-menu v-if="!expiredKillDateTime" open-on-hover location="end" origin="start"
										:open-on-click="false"
										:close-on-content-click="false" content-class="elevation-1">
										<template v-slot:activator="{ props }">
											<v-list-item v-bind="props" link append-icon="mdi-menu-right"
												v-if="!isDisabledEvent">
												<v-list-item-title class="text-body-2">
													Chain Type
												</v-list-item-title>
											</v-list-item>
										</template>
										<v-list>
											<v-list-item v-for="type in chainTypes" :key="type.value" class="pl-2"
												@click.stop="updateChainType(type.value)">
												<template v-slot:prepend>
													<v-avatar rounded="0" size="32px" class="ma-0">
														<Image :img="`${type.icon}.svg`" width="24" height="24"/>
													</v-avatar>
												</template>
												<v-list-item-title class="text-body-2"
													:class="{ 'font-weight-bold': event.Chain === type.value}">
													{{ type.description }}
												</v-list-item-title>
											</v-list-item>
										</v-list>
									</v-menu>
									<v-list-item v-if="!expiredKillDateTime" :disabled="!asset || !isFutureEvent"
										@click.stop="cutEvent">
										<v-list-item-title class="text-body-2">
											Cut Event
										</v-list-item-title>
									</v-list-item>
									<v-list-item v-if="!expiredKillDateTime" :disabled="!asset"
										@click.stop="copyEvent">
										<v-list-item-title class="text-body-2">
											Copy Event
										</v-list-item-title>
									</v-list-item>
									<v-list-item v-if="!expiredKillDateTime" @click.stop="pasteEvent"
										:disabled="(!cuttingEvent && !copiedEvent) || (!isFutureEvent && !asset) || isPlayed || isRemainingTimeRestricted">
										<v-list-item-title class="text-body-2">
											Paste
										</v-list-item-title>
									</v-list-item>
									<v-menu v-if="!expiredKillDateTime" location="bottom end" origin="start"
										:close-on-content-click="false"
										:disabled="!event.Asset" content-class="elevation-1">
										<template v-slot:activator="{ props }">
											<v-list-item v-bind="props" link :disabled="!asset"
												v-if="!isFutureSyndicatedShowEvent">
												<v-list-item-title class="text-body-2">
													Sync to selection
												</v-list-item-title>
											</v-list-item>
										</template>
										<SyncToSelectionConfirmation :message="syncMessage"
											:showEpisode="isSyndicatedShowEvent ? event : null"
											:selectedStation="selectedStation"
											@agree="(...args) => syncToSelection(...args)"
											@cancel="() => contextMenu.open = false"/>
									</v-menu>
									<v-list-item
										v-if="isFutureEvent && !event.Skipped && !isLocalBreak && !expiredKillDateTime"
										:disabled="skipLoading"
										@click.stop="skipEvent({skip: true})">
										<v-list-item-title class="text-body-2">
											Skip Event
										</v-list-item-title>
									</v-list-item>
									<v-list-item v-if="isFutureEvent && event.Skipped"
										:disabled="skipLoading"
										@click.stop="skipEvent({skip: false})">
										<v-list-item-title class="text-body-2">
											Undo Skip
										</v-list-item-title>
									</v-list-item>
								</v-list>
							</v-menu>
						</div>
					</div>
				</v-card>

				<!-- Empty spot event placeholder -->
				<v-card v-else variant="outlined" rounded="0" width="100%">
					<v-container class="text-center">
						There are no Spots in this Spot Block
					</v-container>
				</v-card>
				<div class="drop-target-indicator" :class="targetIndicatorClass" v-if="targetIndicatorIsVisible">
					<v-btn icon width="18" height="18" v-if="dropTargetEvent">
						<v-icon>mdi-plus-circle</v-icon>
					</v-btn>
				</div>
				<v-row no-gutters class="stop-chain-type-warning-line" v-if="chainTypeIsStop">
					<v-col cols="12" class="d-inline-flex align-center justify-center">
						<div class="text-caption">
							{{
								isFutureEvent || isCurrentEvent ? "Audio will stop after this event." :
								"Audio stopped after this event."
							}}
						</div>
						<v-icon size="12" color="#C6002B">mdi-stop-circle</v-icon>
					</v-col>
				</v-row>
				<v-menu v-model="skipSpotBlockReasonMenu.open"
					:offset="[skipSpotBlockReasonMenu.y, skipSpotBlockReasonMenu.x]" :close-on-content-click="false">
					<v-card>
						<v-card-title class="text-h3 font-weight-bold">Select reason for skipping spot</v-card-title>
						<v-divider></v-divider>
						<v-card-text>
							<v-form ref="skipSpotBlockForm" v-model="skipSpotBlockForm.valid">
								<v-radio-group v-model="skipSpotBlockForm.skipType" class="mt-0"
									:rules="[v => !!v || 'Skip type is required']"
									hide-details>
									<v-radio v-for="type in skipTypes" :key="type.value"
										:label="type.description" :value="type.value"
										:ripple="false">
									</v-radio>
								</v-radio-group>
								<v-text-field v-if="skipSpotBlockForm.skipType === 6"
									v-model="skipSpotBlockForm.otherReasonComment"
									placeholder="Enter reason"
									:rules="[v => !!v || 'Reason is required']"
									class="mt-2"
									variant="outlined" density="compact" single-line hide-details>
								</v-text-field>
							</v-form>
						</v-card-text>
						<v-divider></v-divider>
						<v-card-actions>
							<v-spacer></v-spacer>
							<v-btn variant="outlined" color="primary" @click.stop="skipSpotBlockReasonMenu.open = false"
								:disabled="skipSpotBlockForm.loading">
								Cancel
							</v-btn>
							<v-btn variant="flat" color="primary" @click.stop="skipEvent()"
								:disabled="!skipSpotBlockForm.valid || skipSpotBlockForm.loading"
								:loading="skipSpotBlockForm.loading">
								Skip
							</v-btn>
						</v-card-actions>
					</v-card>
				</v-menu>
			</v-hover>
		</v-expansion-panel-title>

		<v-expansion-panel-text v-if="isSpotBlock || isLocalBreak || isDVTBlock">
			<v-expansion-panels class="flex-column" variant="accordion" multiple>
				<draggable v-if="childPlaylistEvents.length > 0"
					:list="childPlaylistEvents"
					:group="{ name: 'log', pull: 'clone', put: ['log', 'library-assets', 'hotkeys']}"
					filter=".not-draggable"
					:force-fallback="true"
					:fallback-tolerance="3"
					:empty-insert-threshold="emptyInsertThreshold"
					drag-class="dragged-log"
					:move="onMove"
					@end="onDropItem"
					:clone="handleDraggedClone">
					<StationLogItem v-for="item in childPlaylistEvents" :key="item.EventID"
						class="pl-1 drop-container"
						:class="{spot: isSpotBlock, 'not-draggable': !parentComponent.isDraggableEvent(item)}"
						:selected-station="selectedStation"
						:event="item"
						:id="item.EventID"
						:data-station-id="event.StationID"
						:data-playlist-id="item.PlaylistID"
						:dropTargetEvent="parentComponent.loadDropTargetEvent(item.EventID)"
						:cuttingEvent="cuttingEvent"
						:copiedEvent="copiedEvent"
						:parentComponent="parentComponent"
						@cut-event="parentComponent.cutEvent(item)"
						@copy-event="parentComponent.copyEvent(item)"
						@paste-event="parentComponent.pasteEvent"
						:type="isSpotBlock ? 'spotBlockEvent' : 'localBreakEvent'"
						data-drop-container="log"/>
				</draggable>
				<StationLogItem v-else
					class="ml-1 empty-spot-placeholder not-draggable"
					:id="`${event.ChildPlaylistID}-00000000-0000-0000-0000-000000000000`"
					:event="emptyBlockContainerEvent"
					:data-playlist-id="event.ChildPlaylistID"/>
			</v-expansion-panels>
		</v-expansion-panel-text>
		<v-divider></v-divider>
		<AuditionAudioWidget v-if="auditionAudioMenu.open && isPlayAsset && asset"
			:menu="auditionAudioMenu"
			:event="event"
			parentComponent="log"
			ref="auditionAudioWidget"
			@close-menu="closeAuditionAudioWidget"/>
	</v-expansion-panel>
</template>

<script>
/* Libraries */
import {VueDraggableNext} from "vue-draggable-next";
import moment from "moment";
import _ from "lodash";

/* Modules */
import AuditionAudioWidget from "@/components/AuditionAudioWidget.vue";
import Image from "@/components/Image.vue";
import SyncToSelectionConfirmation from "@/components/dialogs/SyncToSelectionConfirmation.vue";

/* Enums */
import ConfirmationType from "@/enums/ConfirmationType";
import EventStatus from "@/enums/EventStatus";
import EventTypes from "@/enums/EventTypes";
import POITypes from "@/enums/POITypes";
import ResyncType from "@/enums/ResyncType";
import SequencerStatus from "@/enums/SequencerStatus";

/* Mixins */
import AssetPropertiesMixin from "@/mixins/AssetProperties";
import AuditionAudioMixin from "@/mixins/AuditionAudio";
import DragAndDropMixin from "@/mixins/DragAndDrop";
import DurationFormatMixin from "@/mixins/DurationFormatMixin";
import EventProperties from "@/mixins/EventProperties";

export default {
	name: "StationLogItem",

	components: {
		SyncToSelectionConfirmation: SyncToSelectionConfirmation,
		AuditionAudioWidget,
		draggable: VueDraggableNext,
		Image
	},

	mixins: [
		AssetPropertiesMixin,
		AuditionAudioMixin,
		DragAndDropMixin,
		DurationFormatMixin,
		EventProperties
	],

	emits: ["cutEvent", "copyEvent", "pasteEvent", "menuOpen"],

	props: ["selectedStation", "event", "dropTargetEvent", "cuttingEvent", "copiedEvent", "parentComponent", "type"],

	data() {
		return {
			hoverToPaste: false,
			skipLoading: false,
			skipSpotBlockReasonMenu: {
				open: false,
				x: 0,
				y: 0
			},
			panelWidth: 0
		}
	},

	computed: {
		ConfirmationType() {
			return ConfirmationType
		},

		asset() {
			return this.event.Asset;
		},

		isCurrentEvent() {
			return !this.event.Skipped && this.event.StatusCode === EventStatus.Current;
		},

		isRemainingTimeRestricted() {
			return this.isCurrentEvent ? this.$store.getters.getSequencerItemByEventID(
				this.event.EventID
			)?.RemainingTime < 5 : false;
		},

		missingAudioPath() {
			return this.isPlayAsset && !this.event?.Asset?.POIMarkup?.Path;
		},

		isMissingAudio() {
			return this.isPlayAsset && this.asset && this.missingAudioPath;
		},

		unresolvedTag() {
			if (!this.asset && this.event.ResolveTags) {
				return this.event.ResolveTags.join();
			}
			return false;
		},

		unrecordedVoiceTrack() {
			return this.isVoiceTrack && !this.asset;
		},

		description() {
			return this.event.Description;
		},

		spotBlockContainsLiveRead() {
			return this.isSpotBlock && this.childPlaylistEvents.filter(e => e.LinearAdData?.liveRead).length > 0;
		},

		airTime() {
			let airtime = this.event.AirStartDateTime && this.event.StatusCode !== EventStatus.NotPlayed ?
						  moment.tz(
							  this.event.AirStartDateTime,
							  this.selectedStation.StationTimeZone
						  ).format("HH:mm:ss") :
						  null;

			// In Idle state (playlist has stopped), clear the airtimes for the future events of the current and
			// next hour playlists
			if (this.$store.getters.getSequencerStatus(this.selectedStation.StationID) === SequencerStatus.Idle.val &&
				this.isFutureEvent) {
				const playlist = this.$store.getters.getPlaylistById(
					{stationID: this.selectedStation.StationID, playlistID: this.event.PlaylistID}
				);
				if (moment(playlist.ScheduledStartDateTime).isSameOrBefore(moment().add(1, "hour"))) {
					airtime = null;
				}
			}
			return airtime;
		},

		duration: function () {
			let length = 0;
			if (this.isPlayAsset) {
				length = this.asset?.POIMarkup?.Length ?? 0;
			}
			else if (this.isLiveRead) {
				length = this.event.LinearAdData?.spotLength ?? 0;
			}
			else if (this.isComment) {
				return length;
			}
			else { //SpotBlock or LocalBreak or DVTBlock
				length += this.spotBlockDuration(this.event?.ChildPlaylist?.Events ?? []);
			}
			return length;
		},

		childPlaylistEvents() {
			return _.sortBy(
				this.event.ChildPlaylist?.Events?.filter(
					e => [
						EventTypes.PlayAsset,
						EventTypes.SpotBlock,
						EventTypes.DVTBlock,
						EventTypes.Comment
					].includes(e.EventType)
				),
				"SequenceNumber"
			) ?? [];
		},

		isDisabledEvent() {
			let disabled = false;

			// Check StatusCode
			if (![EventStatus.Ready, EventStatus.Current, EventStatus.Paused].includes(this.event.StatusCode)) {
				disabled = true;
			}

			// Check ResyncType
			if (this.resyncType === ResyncType.Full) {
				disabled = true;
			}

			// Check playlist's HotlineSequence for not now playing events
			if (this.event.StatusCode !== EventStatus.Current) {
				const playlist = this.$store.getters.getPlaylistById(
					{stationID: this.selectedStation.StationID, playlistID: this.event.PlaylistID}
				);
				if (playlist && playlist.HotlineSequence && this.event.SequenceNumber < playlist.HotlineSequence) {
					disabled = true;
				}
			}

			if (this.expiredKillDateTime) {
				return true;
			}

			return disabled;
		},

		syncMessage() {
			const dateNow = moment.tz(this.event.AirStartDateTime, this.selectedStation.StationTimeZone)
								  .format("YYYY-MM-DD HH:mm:ss");
			return `Are you sure you want to sync the Now Playing playback to \n <b>${dateNow}</b>?`;
		},

		targetIndicatorIsVisible() {
			const isDisabled = this.event.empty ? this.event.isDisabled : this.isDisabledEvent;
			const isFuture = this.event.empty ? this.event.isFuture : this.isFutureEvent;

			return !isDisabled && isFuture &&
				   (this.dropTargetEvent ||
					(this.cuttingEvent && (
							(this.hoverToPaste && !this.cuttingEvent.pasteBeforeEvent) ||
							this.cuttingEvent.pasteBeforeEvent === this.event.EventID
						)
					) ||
					(this.copiedEvent && (
							(this.hoverToPaste && !this.copiedEvent.pasteBeforeEvent) ||
							this.copiedEvent.pasteBeforeEvent === this.event.EventID
						)
					)
				   );
		},

		targetIndicatorClass() {
			return this.cuttingEvent ? `insert-after ${this.cuttingEvent.blink ? "blink" : ""}` :
				   this.copiedEvent ? `insert-after ${this.copiedEvent.blink ? "blink" : ""}` :
				   "";
		},

		resyncType() {
			return this.sequencerEvent?.ResyncType ?? this.event.ResyncType;
		},

		emptyBlockContainerEvent() {
			return {
				empty: true,
				EventID: `${this.event.ChildPlaylistID}-00000000-0000-0000-0000-000000000000`,
				isDisabled: this.isDisabledEvent,
				isFuture: this.isFutureEvent
			};
		},

		isSyndicatedShowEvent() {
			return this.isValidURL(this.asset?.POIMarkup?.Path);
		},

		isFutureSyndicatedShowEvent() {
			if (this.isSyndicatedShowEvent) {
				return moment().isBefore(this.asset.ReleaseDateTime);
			}
			return false;
		},

		elapsed() {
			return this.$store.getters.getSequencerItemByEventID(
				this.selectedStation.StationID,
				this.event.EventID
			)?.Elapsed;
		},

		expiredKillDateTime() {
			return moment().isAfter(this.asset?.KillDateTime);
		}
	},

	methods: {
		setColors() {
			let eventTypeColor;
			if (this.isPlayAsset) {
				eventTypeColor = this.asset?.POIMarkup?.Length &&
								 this.assetType?.Color !== null ? this.assetType?.Color :
								 (this.asset?.POIMarkup?.Length ? "rgb(255, 255, 255)" : "rgb(255, 0, 0)");
			}
			else if (this.isSpotBlock) {
				eventTypeColor = this.spotBlockColor;
			}
			else if (this.isLocalBreak) {
				eventTypeColor = this.localBreakColor;
			}
			else if (this.isLiveRead) {
				eventTypeColor = this.liveReadColor;
			}
			else if (this.isComment) {
				eventTypeColor = this.commentColor;
			}
			else if (this.isDVTBlock) {
				eventTypeColor = this.DVTBlockEventColor;
			}

			if (eventTypeColor) {
				let eventElement = this.$el.querySelector(".log-event-container");
				eventElement.style.setProperty("--eventTypeColorRGBValues", eventTypeColor.match(/\d+/g).join(","));
			}
		},

		spotBlockDuration(childPlaylistEvents) {
			let duration = 0;
			const events = childPlaylistEvents.filter(e => !e.Skipped && [
				EventTypes.PlayAsset,
				EventTypes.SpotBlock,
				EventTypes.DVTBlock,
				EventTypes.Comment
			].includes(e.EventType));

			events.forEach((e) => {
				if (e.EventType === EventTypes.SpotBlock || e.EventType === EventTypes.DVTBlock) {
					duration += this.spotBlockDuration(e.ChildPlaylist?.Events ?? []);
				}
				else {
					duration += e.Asset?.POIMarkup?.Length ?? 0;
				}
			});
			return duration;
		},

		onMove(evt, originalEvent) {
			const draggedElement = document.querySelector(".dragged-log");
			this.onDragItem(evt, originalEvent, draggedElement);
			return false;
		},

		openAssetManagement() {
			this.$store.commit("setAssetManagementItem", this.event);
		},

		openSkipSpotBlockReasonMenu(ev) {
			this.skipSpotBlockReasonMenu.open = true;
			this.skipSpotBlockReasonMenu.x = ev.clientX + 50;
			this.skipSpotBlockReasonMenu.y = ev.clientY - 50;

			if (this.$refs.skipSpotBlockForm) {
				this.$refs.skipSpotBlockForm.reset();
			}
		},

		syncToSelection({switchToAutoMode = true, syncInput = {}} = {}) {
			let msg = "";
			if (!_.isNil(switchToAutoMode)) {
				this.$store.commit(
					"setSwitchToAutoMode",
					{stationID: this.selectedStation.StationID, switchToAutoMode}
				);
			}
			if (this.isSyndicatedShowEvent) {
				const playlist = this.$store.getters.getPlaylistById(
					{stationID: this.selectedStation.StationID, playlistID: this.event.PlaylistID}
				);
				const duration = moment(playlist.ScheduledEndDateTime).diff(playlist.ScheduledStartDateTime, "seconds");

				const fadeOutPOI = this.event.OverridePOIMarkup.POIMarkers
									   .find(m => m.POIType === POITypes.FadeOut)?.Position ?? duration;
				const startLiveShowPOI = this.event.OverridePOIMarkup.POIMarkers
											 .find(m => m.POIType === POITypes.StartLiveShow)?.Position ??
										 this.asset.POIMarkup.POIMarkers
											 .find(m => m.POIType === POITypes.StartLiveShow)?.Position ?? 0;

				const OriginalOverridePOIStart = Math.min(fadeOutPOI - duration, 0);

				let syncPosition = moment.duration({
					hours: syncInput.hours,
					minutes: syncInput.minutes,
					seconds: syncInput.seconds
				}).asSeconds();

				// We need to make sure that the position is not in the future of a live asset and
				// also not very close to live
				const assetReleaseDateTime = moment(this.event.Asset.ReleaseDateTime);
				if (assetReleaseDateTime.clone().add(syncPosition, "seconds")
										.isSameOrAfter(moment().add(6, "seconds"))
				) {
					syncPosition = moment().diff(assetReleaseDateTime, "seconds") - 6;
				}

				if (syncPosition < 0) {
					syncPosition = 0;
				}

				msg = {
					RestartPosition: OriginalOverridePOIStart + syncPosition + startLiveShowPOI
				};
				console.log("RestartPosition: ", moment.duration(msg.RestartPosition, "seconds").format("hh:mm:ss"));
			}

			this.$store.dispatch("sequencerSyncActionRequest", {
				action: `SyncToEvent.${this.event.EventID}`,
				msg,
				stationID: this.selectedStation.StationID
			});
			this.contextMenu.open = false;
		},

		cutEvent() {
			this.$emit("cutEvent");
			this.contextMenu.open = false;

			if (this.$refs.auditionAudioWidget?.waveform) {
				this.$refs.auditionAudioWidget.stopAudioWaveform();
			}
		},

		copyEvent() {
			this.$emit("copyEvent");
			this.contextMenu.open = false;

			if (this.$refs.auditionAudioWidget?.waveform) {
				this.$refs.auditionAudioWidget.stopAudioWaveform();
			}
		},

		pasteEvent() {
			this.$emit("pasteEvent", this.event, this.$el);
			this.contextMenu.open = false;

			if (this.$refs.auditionAudioWidget?.waveform) {
				this.$refs.auditionAudioWidget.stopAudioWaveform();
			}
		},

		isValidURL(url) {
			try {
				return Boolean(new URL(url));
			} catch (e) {
				return false;
			}
		}
	},

	watch: {
		"event.Skipped"() {
			this.skipLoading = false;
		},

		"contextMenu.open"(open) {
			this.$emit("menuOpen", open);
		}
	},

	mounted() {
		// Close other open context menus on click
		window.addEventListener("click", this.closeContextMenu);
	},

	beforeUnmount() {
		window.removeEventListener("click", this.closeContextMenu);
	}
}
</script>

<style lang="scss" scoped>
.v-card:not(.skip-spot-block-reason-container):not(.confirmation-card) {
	background: rgba(var(--eventTypeColorRGBValues), .07);
	border: none;
	margin-bottom: 1px;
	-webkit-user-select: none; /* Safari */
	-ms-user-select: none; /* IE 10 and IE 11 */
	user-select: none; /* Standard syntax */

	.asset-container {
		border-width: 0 0 0 8px;
		border-color: rgba(var(--eventTypeColorRGBValues), 1);
		border-style: solid;
		height: 50px;
		padding: 0 0 0 8px;
		position: relative;

		.pot-number {
			background: #ffffff;
			border: 1px solid rgb(var(--eventTypeColorRGBValues));
			border-radius: 2px;
			color: $grey-400;
			font-size: 24px;
			font-weight: 700;
			line-height: 36px;
			min-width: 30px;
			padding: 1px 5px;
			text-align: center;

			&.empty {
				background: transparent;
				border-color: transparent !important;
			}

			@media only screen and (max-width: 1920px) {
				padding: 1px 2px;
			}
		}

		.airtime {
			min-width: 110px;
		}

		.now-playing-label {
			color: rgb(var(--eventTypeColorRGBValues));
			font-size: 12px;
			font-weight: 700;
			display: inline-flex;
			align-items: center;
			position: relative;
			bottom: 3px;

			> .v-icon {
				color: rgb(var(--eventTypeColorRGBValues));
			}
		}

		.chain-type {
			min-width: 60px;
		}

		.asset-type-icon {
			color: rgba(var(--eventTypeColorRGBValues), 1);
			margin-right: 8px;
			min-width: 35px;
		}

		.timer {
			min-width: 60px;
		}

		.asset-actions {
			min-width: 60px;

			.v-btn {
				min-width: 30px;
			}
		}
	}

	&.disabled-event {
		background: rgba(113, 114, 119, .12);

		.asset-container {
			border-color: rgba(var(--eventTypeColorRGBValues), 1);

			.pot-number:not(.empty) {
				border-color: $grey-400;
				color: $grey-300;
			}

			.event-field,
			.event-field .v-icon,
			.asset-actions * {
				color: $grey-400;
			}
		}

		:deep(.v-field__input) {
			cursor: default;
		}
	}


	&.skipped-event {
		background: $ihm-grey1;

		.asset-container {
			border-color: rgba(var(--eventTypeColorRGBValues), 1);

			.pot-number:not(.empty) {
				border-color: $grey-400;
				color: $grey-300;
			}

			.event-field,
			.event-field .v-icon,
			.asset-actions * {
				color: $grey-300;
			}
		}

		:deep(.v-field__input) {
			cursor: default;
		}
	}

	&.missing-audio {
		background: #484C4F !important;
		outline: 1px dashed #2D2D2D;

		.v-icon:not(.mdi-alert, .mdi-eject, .mdi-information-outline, .asset-type-icon, .mdi-cancel) {
			color: #E22100 !important;
			background: radial-gradient(white 50%, transparent 50%);
		}

		.v-icon:not(:not(.mdi-information-outline, .asset-type-icon, .mdi-cancel, .mdi-alert)) {
			color: white !important;
		}

		:hover {
			.v-icon:not(.mdi-close-circle,.mdi-play,.asset-type-icon) {
				color: white !important;
			}
		}
	}

	&.unrecorded-vt {
		background: rgba(255, 218, 35, 0.55) !important;
		outline: 1px dashed #C19703;
	}

	.stop-chain-type-warning-line {
		height: 24px;
		background: $ihm-grey1;

		div {
			letter-spacing: .5px !important;
		}
	}

	.gap-time {
		color: $ihm-orange1;
		font-weight: bold;
	}

	.live-read {
		background: $ihm-white;
		color: $ihm-red2;
	}

	&.v-theme--dark {
		background-color: transparent;

		.asset-container {
			color: rgba(var(--eventTypeColorRGBValues), 1);

			.v-image {
				filter: invert(100%) sepia(100%) saturate(0%) brightness(300%) !important;
			}

			.v-icon.mdi-information-outline {
				color: rgba(var(--eventTypeColorRGBValues), 1);
			}

			.pot-number {
				background-color: transparent;
				border-color: rgba(var(--eventTypeColorRGBValues), 1);
				color: $grey-300;
			}

			&.now-playing {
				background-color: rgba(var(--eventTypeColorRGBValues), 0.61);
				border-width: 1px 1px 1px 8px;
				color: $ihm-white;

				.now-playing-label {
					color: $ihm-white;

					> .v-icon {
						color: $ihm-white;
					}
				}

				.pot-number,
				.v-icon.mdi-information-outline {
					color: $ihm-white;
				}
			}
		}

		&.disabled-event {
			background-color: rgba(55, 57, 61, 1);

			.asset-container {
				.event-field,
				.event-field .v-icon,
				.asset-actions * {
					color: $grey-300;
				}

				.v-image {
					filter: unset !important;
				}

				.v-icon.mdi-information-outline {
					color: $grey-300;
				}

				.pot-number {
					border-color: $grey-300;
				}
			}
		}

		.live-read {
			background: $ihm-red2;
			color: $ihm-white;
		}
	}
}

.v-menu__content.v-theme--dark {
	.v-list-item__avatar > .v-image {
		filter: invert(100%) sepia(100%) saturate(0%) brightness(300%) !important;
	}
}

.stop-chain-type-warning-line {
	height: 24px;
	background: $ihm-grey1;
	width: 100%;
	border-top: 1px solid $grey-200;

	div {
		color: $grey-300;
		font-size: 10px !important;
		letter-spacing: .2px !important;
		margin-right: 8px;
	}
}

.drop-target-indicator {
	background-color: $ihm-orange;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	height: 4px;
	width: 100%;
	position: absolute;
	z-index: 3;
	transition: all 0.5s ease;
	bottom: 0;

	> .v-btn {
		background-color: $ihm-white;
		color: $ihm-orange !important;
	}
}
</style>
